import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2, Power, Check, X, Users as UsersIcon, FileUp, Trash2, Pencil } from "lucide-react";
import Layout from "@/components/Layout";
import { Panel, SortTh, useTableSort } from "@/components/widgets";
import DataImport from "@/components/DataImport";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiError } from "@/lib/api";
import { fmtInt, fmtDateTime } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const TYPES = [
  { key: "customers", label: "Customers" },
  { key: "programmers", label: "Programmers" },
  { key: "grades", label: "Material Grades" },
  { key: "ownership", label: "Ownership" },
  { key: "thicknesses", label: "Thicknesses" },
  { key: "machines", label: "Machines" },
];

function MasterTab({ type, isThickness }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canManage = can("master.manage");
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [thk, setThk] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [delItem, setDelItem] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["master-admin", type],
    queryFn: () => client.get(`/master-data/${type}?includeInactive=true`).then((r) => r.data.data),
  });

  const { sorted, sort, dir, onSort } = useTableSort(data, "display_name", "asc", {
    display_name: (r) => r.display_name, usageCount: (r) => r.usageCount, active: (r) => (r.active ? 1 : 0),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["master-admin", type] });
    qc.invalidateQueries({ queryKey: ["masters"] });
    qc.invalidateQueries({ queryKey: ["master", type] });
  };

  const add = async () => {
    setBusy(true);
    try {
      const body = isThickness ? { displayName: `${thk} mm`, thicknessMm: parseFloat(thk) } : { displayName: name.trim() };
      await client.post(`/master-data/${type}`, body);
      toast.success("Added"); setAdding(false); setName(""); setThk(""); invalidate();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const rename = async (id) => {
    setBusy(true);
    try {
      await client.patch(`/master-data/${type}/${id}`, { displayName: editName.trim() });
      toast.success("Renamed"); setEditId(null); invalidate();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = async (item) => {
    try {
      await client.patch(`/master-data/${type}/${item.id}`, { active: !item.active });
      toast.success(item.active ? "Deactivated" : "Reactivated"); invalidate();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    try {
      await client.delete(`/master-data/${type}/${delItem.id}`);
      toast.success("Deleted"); setDelItem(null); invalidate();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDelItem(null); }
  };

  return (
    <Panel testid={`master-panel-${type}`}
      action={canManage && !adding && (
        <Button size="sm" className="text-white" style={{ background: "#0D9488" }} onClick={() => setAdding(true)} data-testid={`master-add-${type}`}>
          <Plus size={15} className="mr-1" /> Add
        </Button>
      )}>
      {adding && (
        <div className="flex gap-2 mb-4">
          {isThickness ? (
            <Input type="number" step="0.1" placeholder="Thickness (mm)" value={thk} onChange={(e) => setThk(e.target.value)} data-testid={`master-input-${type}`} />
          ) : (
            <Input placeholder="New value name" value={name} onChange={(e) => setName(e.target.value)} data-testid={`master-input-${type}`} />
          )}
          <Button size="sm" className="text-white" style={{ background: "#0D9488" }} onClick={add} disabled={busy || (isThickness ? !thk : !name.trim())} data-testid={`master-save-${type}`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : "Save"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b" style={{ borderColor: "#E2E8F0" }}>
                <SortTh label="Name" k="display_name" sort={sort} dir={dir} onSort={onSort} />
                <SortTh label="Usage" k="usageCount" sort={sort} dir={dir} onSort={onSort} right />
                <SortTh label="Status" k="active" sort={sort} dir={dir} onSort={onSort} />
                {canManage && <th className="py-2 px-3 font-medium text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {(sorted || []).map((item) => (
                <tr key={item.id} className="border-b hover:bg-slate-50" style={{ borderColor: "#F1F5F9" }} data-testid={`master-row-${item.id}`}>
                  <td className="py-2.5 px-3">
                    {editId === item.id ? (
                      <div className="flex items-center gap-1">
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 max-w-xs" data-testid="master-edit-input" />
                        <Button size="sm" variant="ghost" onClick={() => rename(item.id)} data-testid="master-edit-save"><Check size={15} className="text-emerald-600" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X size={15} /></Button>
                      </div>
                    ) : (
                      <span className={item.active ? "text-slate-800" : "text-slate-400 line-through"}>{item.display_name}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-xs text-slate-500">{fmtInt(item.usageCount)}</td>
                  <td className="py-2.5 px-3">
                    <span className="text-xs font-medium px-2 py-0.5 rounded" style={item.active ? { background: "#DCFCE7", color: "#15803D" } : { background: "#F1F5F9", color: "#64748B" }}>
                      {item.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {canManage && (
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditId(item.id); setEditName(item.display_name); }} data-testid={`master-rename-${item.id}`}>
                          <Pencil size={13} className="mr-1" /> Rename
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => toggle(item)} data-testid={`master-toggle-${item.id}`} title={item.active ? "Deactivate" : "Reactivate"}>
                          <Power size={14} className={item.active ? "text-amber-600" : "text-emerald-600"} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7" disabled={item.usageCount > 0}
                          onClick={() => setDelItem(item)} data-testid={`master-delete-${item.id}`}
                          title={item.usageCount > 0 ? "In use — deactivate instead" : "Delete"}>
                          <Trash2 size={14} className={item.usageCount > 0 ? "text-slate-300" : "text-red-500"} />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{delItem?.display_name}"?</AlertDialogTitle>
            <AlertDialogDescription>This value has never been used and will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-master-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}

const EMPTY_USER = { email: "", displayName: "", role: "capture", password: "" };

function UserDialog({ open, onClose, editing, roles }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(editing ? { ...editing, password: "" } : EMPTY_USER);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await client.patch(`/users/${editing.id}`, {
          displayName: form.displayName, role: form.role, password: form.password || undefined,
        });
        toast.success("User updated");
      } else {
        await client.post("/users", { email: form.email.trim().toLowerCase(), displayName: form.displayName, role: form.role, password: form.password });
        toast.success("User created");
      }
      qc.invalidateQueries({ queryKey: ["users"] });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{editing ? "Edit User" : "New User"}</DialogTitle>
          <DialogDescription className="sr-only">Manage platform user account details and role.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Email</Label>
            <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => set("email", e.target.value)} data-testid="user-email" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Display name</Label>
            <Input value={form.displayName} onChange={(e) => set("displayName", e.target.value)} data-testid="user-displayname" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{(roles || []).map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">{editing ? "New password (optional)" : "Password"}</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={editing ? "Leave blank to keep current" : "At least 6 characters"} data-testid="user-password" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 text-white" style={{ background: "#0D9488" }} onClick={save}
            disabled={busy || !form.displayName || (!editing && (!form.email || form.password.length < 6))} data-testid="user-save">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [dialog, setDialog] = useState(null); // {editing} | {}
  const [del, setDel] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["users"], queryFn: () => client.get("/users").then((r) => r.data.data) });
  const { data: roles } = useQuery({ queryKey: ["roles"], queryFn: () => client.get("/roles").then((r) => r.data.data) });

  const { sorted, sort, dir, onSort } = useTableSort(data, "displayName", "asc", {
    displayName: (r) => r.displayName, email: (r) => r.email, roleLabel: (r) => r.roleLabel, lastLoginAt: (r) => r.lastLoginAt || "",
  });

  const remove = async () => {
    try {
      await client.delete(`/users/${del.id}`);
      toast.success("User deleted"); setDel(null); qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDel(null); }
  };

  return (
    <Panel title="Platform Users" testid="users-panel"
      action={<Button size="sm" className="text-white" style={{ background: "#0D9488" }} onClick={() => setDialog({})} data-testid="user-add-btn"><Plus size={15} className="mr-1" /> New User</Button>}>
      {isLoading ? <Skeleton className="h-40 w-full" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b" style={{ borderColor: "#E2E8F0" }}>
              <SortTh label="Name" k="displayName" sort={sort} dir={dir} onSort={onSort} />
              <SortTh label="Email" k="email" sort={sort} dir={dir} onSort={onSort} />
              <SortTh label="Role" k="roleLabel" sort={sort} dir={dir} onSort={onSort} />
              <SortTh label="Last Login" k="lastLoginAt" sort={sort} dir={dir} onSort={onSort} />
              <th className="py-2 px-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {(sorted || []).map((u) => (
                <tr key={u.id} className="border-b hover:bg-slate-50" style={{ borderColor: "#F1F5F9" }} data-testid={`user-row-${u.id}`}>
                  <td className="py-2.5 px-3">{u.displayName}</td>
                  <td className="py-2.5 px-3 font-mono text-xs">{u.email}</td>
                  <td className="py-2.5 px-3"><span className="text-xs px-2 py-0.5 rounded" style={{ background: "#EDE9FE", color: "#6D28D9" }}>{u.roleLabel}</span></td>
                  <td className="py-2.5 px-3 text-xs text-slate-500">{u.lastLoginAt ? fmtDateTime(u.lastLoginAt) : "Never"}</td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDialog({ editing: u })} data-testid={`user-edit-${u.id}`}><Pencil size={13} className="mr-1" /> Edit</Button>
                      <Button size="sm" variant="ghost" className="h-7" disabled={u.id === me.id} onClick={() => setDel(u)} data-testid={`user-delete-${u.id}`}>
                        <Trash2 size={14} className={u.id === me.id ? "text-slate-300" : "text-red-500"} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog && <UserDialog open onClose={() => setDialog(null)} editing={dialog.editing} roles={roles} />}

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {del?.displayName}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the user account and their access.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-user-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}

function ReassignCustomers() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [reason, setReason] = useState("");
  const [del, setDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["master-admin", "customers"],
    queryFn: () => client.get("/master-data/customers?includeInactive=true").then((r) => r.data.data),
  });
  const customers = data || [];
  const fromCust = customers.find((c) => c.id === fromId);

  const close = () => { setOpen(false); setFromId(""); setToId(""); setReason(""); setDel(false); };
  const submit = async () => {
    setBusy(true);
    try {
      const { data: body } = await client.post("/master-data/customers/reassign", { fromId, toId, reason, deleteSource: del });
      toast.success(`Reassigned ${body.data.reassigned} record(s) to ${body.data.to}${body.data.sourceDeleted ? " · source deleted" : ""}`);
      qc.invalidateQueries();
      close();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!can("nesting.correct")) return null;
  return (
    <div className="mb-4 flex items-center justify-between panel px-4 py-3 bg-violet-50" style={{ borderColor: "#C4B5FD" }}>
      <p className="text-xs text-slate-600">Merge duplicate customers by moving all their records to the correct account, then optionally delete the duplicate.</p>
      <Button size="sm" className="text-white shrink-0" style={{ background: "#7C3AED" }} onClick={() => setOpen(true)} data-testid="reassign-open-btn">
        Merge / Reassign
      </Button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Reassign customer records</DialogTitle>
            <DialogDescription className="sr-only">Move all nesting records from one customer to another in a single action.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">From (duplicate) customer</Label>
              <Select value={fromId} onValueChange={setFromId}>
                <SelectTrigger data-testid="reassign-from"><SelectValue placeholder="Select source customer" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name} ({c.usageCount})</SelectItem>)}
                </SelectContent>
              </Select>
              {fromCust && <p className="text-[11px] text-slate-500 mt-1">{fmtInt(fromCust.usageCount)} record(s) will be moved.</p>}
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">To (correct) customer</Label>
              <Select value={toId} onValueChange={setToId}>
                <SelectTrigger data-testid="reassign-to"><SelectValue placeholder="Select target customer" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {customers.filter((c) => c.id !== fromId).map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Reason (kept in audit trail)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate customer merge" data-testid="reassign-reason" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={del} onChange={(e) => setDel(e.target.checked)} data-testid="reassign-delete-source" className="w-4 h-4 accent-violet-600" />
              Delete the source customer after moving its records
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={close}>Cancel</Button>
            <Button className="flex-1 text-white" style={{ background: "#0D9488" }} onClick={submit}
              disabled={busy || !fromId || !toId || fromId === toId || !reason.trim()} data-testid="reassign-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Reassign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Settings() {
  const { can } = useAuth();
  return (
    <Layout title="Settings & Master Data" subtitle="Shared controlled lists across all users" showFilters={false}>
      <div className="animate-fade-up">
        <Tabs defaultValue="customers">
          <TabsList className="bg-slate-100 flex-wrap h-auto" data-testid="settings-tabs">
            {TYPES.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`settings-tab-${t.key}`}>{t.label}</TabsTrigger>)}
            {can("admin.users") && <TabsTrigger value="users" data-testid="settings-tab-users"><UsersIcon size={14} className="mr-1" /> Users</TabsTrigger>}
            {can("nesting.create") && <TabsTrigger value="import" data-testid="settings-tab-import"><FileUp size={14} className="mr-1" /> Data Import</TabsTrigger>}
          </TabsList>
          {TYPES.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              {t.key === "customers" && <ReassignCustomers />}
              <MasterTab type={t.key} isThickness={t.key === "thicknesses"} />
            </TabsContent>
          ))}
          {can("admin.users") && <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>}
          {can("nesting.create") && <TabsContent value="import" className="mt-4"><DataImport /></TabsContent>}
        </Tabs>
      </div>
    </Layout>
  );
}
